import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import {
  userGroupService,
  UpdateUserGroupRequest,
  UserGroup,
} from '../../services/userGroupService'

interface UserGroupEditModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  userGroup: UserGroup
}

const UserGroupEditModal: React.FC<UserGroupEditModalProps> = ({
  open,
  onClose,
  onSuccess,
  userGroup,
}) => {
  const [formData, setFormData] = useState<UpdateUserGroupRequest>({
    name: '',
    description: '',
    department: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // userGroupが変更されたときにフォームデータを初期化
  useEffect(() => {
    if (userGroup) {
      setFormData({
        name: userGroup.name,
        description: userGroup.description || '',
        department: userGroup.department || '',
      })
    }
  }, [userGroup])

  const handleInputChange =
    (field: keyof UpdateUserGroupRequest) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }))
      // エラーをクリア
      if (error) setError(null)
    }

  const handleSubmit = async () => {
    // バリデーション
    if (!formData.name.trim()) {
      setError('グループ名は必須です')
      return
    }

    if (formData.name.length > 100) {
      setError('グループ名は100文字以下で入力してください')
      return
    }

    if (formData.description && formData.description.length > 500) {
      setError('説明は500文字以下で入力してください')
      return
    }

    if (formData.department && formData.department.length > 100) {
      setError('部門名は100文字以下で入力してください')
      return
    }

    try {
      setLoading(true)
      setError(null)

      // 空文字列をundefinedに変換
      const requestData: UpdateUserGroupRequest = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        department: formData.department?.trim() || undefined,
      }

      await userGroupService.updateUserGroup(userGroup.id, requestData)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'グループの更新に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    setError(null)
    onClose()
  }

  // 変更があるかチェック
  const hasChanges = () => {
    return (
      formData.name !== userGroup.name ||
      formData.description !== (userGroup.description || '') ||
      formData.department !== (userGroup.department || '')
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          ユーザーグループ編集
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            グループの基本情報を編集します。メンバーや受講割り当ては別途管理されます。
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="グループ名"
            value={formData.name}
            onChange={handleInputChange('name')}
            margin="normal"
            required
            disabled={loading}
            error={formData.name.length > 100}
            helperText={`${formData.name.length}/100文字`}
            placeholder="例: 開発チーム、営業部グループ"
          />

          <TextField
            fullWidth
            label="説明"
            value={formData.description}
            onChange={handleInputChange('description')}
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
            error={(formData.description?.length || 0) > 500}
            helperText={`${formData.description?.length || 0}/500文字（任意）`}
            placeholder="グループの目的や特徴を説明してください"
          />

          <TextField
            fullWidth
            label="部門"
            value={formData.department}
            onChange={handleInputChange('department')}
            margin="normal"
            disabled={loading}
            error={(formData.department?.length || 0) > 100}
            helperText={`${formData.department?.length || 0}/100文字（任意）`}
            placeholder="例: 開発部、営業部、総務部"
          />

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>作成者:</strong> {userGroup.creator.name} ({userGroup.creator.email})
              <br />
              <strong>作成日:</strong> {new Date(userGroup.createdAt).toLocaleDateString('ja-JP')}
              <br />
              <strong>メンバー数:</strong> {userGroup.members.length}名
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name.trim() || !hasChanges()}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? '更新中...' : '更新'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserGroupEditModal
